// frontend/src/pages/user/MyOrdersPage.tsx
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useMyOrders } from '../../hooks/useMyOrders'
import { useCancelOrder } from '../../hooks/useCancelOrder'
import { OrderRow } from '../../components/p2p/OrderRow'
import { cn } from '../../lib/utils'

type Tab = 'active' | 'history'

export function MyOrdersPage() {
  const [tab, setTab] = useState<Tab>('active')
  const { data, isLoading } = useMyOrders()
  const { cancel, pendingId } = useCancelOrder()

  const counts = useMemo(() => {
    if (!data) return { active: 0, history: 0 }
    return {
      active: data.filter((o) => o.status === 'ACTIVE' || o.status === 'PARTIAL').length,
      history: data.filter((o) => ['FILLED', 'CANCELLED', 'EXPIRED'].includes(o.status)).length,
    }
  }, [data])

  const filtered = useMemo(() => {
    if (!data) return []
    if (tab === 'active') {
      return data.filter((o) => o.status === 'ACTIVE' || o.status === 'PARTIAL')
    }
    return data.filter((o) => ['FILLED', 'CANCELLED', 'EXPIRED'].includes(o.status))
  }, [data, tab])

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <header className="space-y-2">
        <Link to="/p2p" className="inline-flex items-center gap-1 text-xs text-dark-500 hover:text-uni-400 transition-colors">
          <ArrowLeft className="w-3 h-3" />
          Volver al intercambio
        </Link>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-white">Mis órdenes</h1>
      </header>

      <div className="flex flex-wrap gap-2">
        <TabButton active={tab === 'active'} onClick={() => setTab('active')} count={counts.active}>
          Activas
        </TabButton>
        <TabButton active={tab === 'history'} onClick={() => setTab('history')} count={counts.history}>
          Historial
        </TabButton>
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <SkeletonRow />
        ) : filtered.length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          filtered.map((o) => (
            <OrderRow
              key={o.orderId}
              order={o}
              onCancel={tab === 'active' ? cancel : undefined}
              cancelPending={pendingId === o.orderId}
            />
          ))
        )}
      </div>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean
  onClick: () => void
  count: number
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all',
        active
          ? 'bg-uni-500/15 text-uni-400 border-uni-500/40 shadow-glow-sm'
          : 'bg-dark-800/50 text-dark-400 border-dark-700 hover:text-white',
      )}
    >
      {children}
      <span className={cn('text-[10px] font-mono px-1.5 py-0.5 rounded', active ? 'bg-uni-500/20 text-uni-300' : 'bg-dark-700 text-dark-400')}>
        {count}
      </span>
    </button>
  )
}

function SkeletonRow() {
  return (
    <div className="rounded-xl border border-dark-800 bg-dark-900/40 p-4 animate-pulse">
      <div className="h-4 bg-dark-800 rounded w-1/3 mb-3" />
      <div className="h-8 bg-dark-800 rounded w-2/3 mb-3" />
      <div className="h-1.5 bg-dark-800 rounded w-full" />
    </div>
  )
}

function EmptyState({ tab }: { tab: Tab }) {
  return (
    <div className="rounded-xl border border-dashed border-dark-800 bg-dark-900/30 p-10 text-center space-y-3">
      <div className="text-3xl text-dark-700">∅</div>
      <p className="text-sm text-dark-400">
        {tab === 'active' ? 'No tienes órdenes activas' : 'Aún no tienes histórico'}
      </p>
      {tab === 'active' && (
        <Link
          to="/p2p"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-uni-500/15 text-uni-400 border border-uni-500/30 hover:bg-uni-500/25 transition-colors"
        >
          Crear primera orden →
        </Link>
      )}
    </div>
  )
}
